'use client';

import { FormEvent, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calculator, Truck, Wrench, Euro, AlertCircle, Download, CreditCard } from 'lucide-react';

type Locale = 'nl' | 'de' | 'en' | 'fr';

const copy: Record<Locale, {
  title: string; subtitle: string;
  location: string; locationPlaceholder: string;
  city: string; postalCode: string; country: string;
  workType: string; workTypePlaceholder: string;
  description: string; descriptionPlaceholder: string;
  productInfo: string; productInfoPlaceholder: string;
  height: string; heightLabel: string; heightOptions: string[];
  surface: string; surfaceLabel: string; surfaceOptions: string[];
  access: string; accessLabel: string; accessOptions: string[];
  calculate: string; calculating: string;
  loading: string; disclaimer: string;
  quote: string; estimateNote: string;
  distance: string; labor: string; materials: string; subtotal: string; vat: string; total: string;
  accept: string;
  fallback: string; instructions: string;
  perKm: string; returnTrip: string; hours: string; perHour: string;
  includingTravel: string;
}> = {
  nl: {
    title: 'Montage & Installatie',
    subtitle: 'Laat je producten professioneel monteren door een signbedrijf bij jou in de buurt.',
    location: 'Adres', locationPlaceholder: 'Straat + huisnummer',
    city: 'Plaats', postalCode: 'Postcode', country: 'Land',
    workType: 'Type werkzaamheden', workTypePlaceholder: 'Kies het type montage...',
    description: 'Beschrijving werkzaamheden', descriptionPlaceholder: 'Beschrijf wat er gemonteerd moet worden. Wees zo specifiek mogelijk qua afmetingen, hoogte, materiaal etc.',
    productInfo: 'Te monteren product(en)', productInfoPlaceholder: 'Wat moet er gemonteerd worden? Bijv: "Lichtbak 200x100cm, full-color"',
    height: 'Werkhoogte', heightLabel: 'Tot welke hoogte wordt er gewerkt?', heightOptions: ['< 2.5m (ladder)', '2.5m - 5m (rolsteiger)', '5m - 10m (hoogwerker)', '> 10m (specialistische hoogwerker)'],
    surface: 'Ondergrond', surfaceLabel: 'Op welke ondergrond wordt gemonteerd?', surfaceOptions: ['Baksteen / metselwerk', 'Beton', 'Hout', 'Metaal / aluminium', 'Glas', 'Binnenwand (gips/pleister)', 'Plafond / systeemplafond', 'Anders / onbekend'],
    access: 'Toegankelijkheid', accessLabel: 'Hoe is de montageplek bereikbaar?', accessOptions: ['Vrij toegankelijk (begane grond)', 'Eerste verdieping (ladder/steiger nodig)', 'Alleen met hoogwerker bereikbaar', 'Slecht bereikbaar / lastige hoek', 'Binnen (normale toegang)', 'Binnen (krappe doorgang)'],
    calculate: 'Offerte berekenen', calculating: 'Berekenen...',
    loading: 'Offerte wordt berekend...',
    disclaimer: '* Dit is een richtprijs op basis van de door jou verstrekte informatie. Als de werkelijke situatie afwijkt van je beschrijving, kan de eindfactuur hoger uitvallen. De definitieve prijs wordt vastgesteld na inspectie door het signbedrijf.',
    quote: 'Offerte', estimateNote: 'Richtprijs — definitieve prijs na inspectie ter plaatse',
    distance: 'Reiskosten', labor: 'Arbeidsloon', materials: 'Materialen', subtotal: 'Subtotaal', vat: 'BTW (21%)', total: 'Totaal incl. BTW',
    accept: 'Offerte accepteren en bestellen',
    fallback: 'Offerte op basis van schatting. Neem contact op voor exacte prijs.',
    instructions: 'Vul je gegevens en het montage-adres in. Kies het type werkzaamheden en beschrijf de klus. Je ontvangt een richtprijs voor alleen de montage. Het product bestel je apart via de productpagina.',
    perKm: '/km', returnTrip: 'retour', hours: 'uur', perHour: '/uur',
    includingTravel: 'incl. reistijd',
  },
  de: {
    title: 'Montage & Installation', subtitle: 'Lassen Sie Ihre Produkte professionell von einem Schilderbetrieb in Ihrer Nahe montieren.',
    location: 'Adresse', locationPlaceholder: 'Strasse + Hausnummer',
    city: 'Stadt', postalCode: 'PLZ', country: 'Land',
    workType: 'Art der Arbeit', workTypePlaceholder: 'Wahlen Sie die Montageart...',
    description: 'Beschreibung', descriptionPlaceholder: 'Beschreiben Sie, was montiert werden soll. Seien Sie moglichst genau mit Massen, Hohe, Material etc.',
    productInfo: 'Produkt(e)', productInfoPlaceholder: 'Was soll montiert werden? Z.B.: "Leuchtkasten 200x100cm, Vollfarbe"',
    height: 'Arbeitshohe', heightLabel: 'Bis zu welcher Hohe wird gearbeitet?', heightOptions: ['< 2.5m (Leiter)', '2.5m - 5m (Rollgerust)', '5m - 10m (Hubsteiger)', '> 10m (Spezial-Hubsteiger)'],
    surface: 'Untergrund', surfaceLabel: 'Auf welchem Untergrund wird montiert?', surfaceOptions: ['Ziegel / Mauerwerk', 'Beton', 'Holz', 'Metall / Aluminium', 'Glas', 'Innenwand (Gips/Putz)', 'Decke / Systemdecke', 'Anders / unbekannt'],
    access: 'Zuganglichkeit', accessLabel: 'Wie ist der Montageort erreichbar?', accessOptions: ['Frei zuganglich (Erdgeschoss)', 'Erste Etage (Leiter/Gerust)', 'Nur mit Hubsteiger', 'Schwer zuganglich', 'Innen (normaler Zugang)', 'Innen (enger Durchgang)'],
    calculate: 'Angebot berechnen', calculating: 'Berechne...',
    loading: 'Angebot wird berechnet...',
    disclaimer: '* Dies ist ein Richtpreis basierend auf Ihren Angaben. Weicht die tatsachliche Situation von Ihrer Beschreibung ab, kann die Schlussrechnung hoher ausfallen. Der endgultige Preis wird nach Begutachtung durch den Schilderbetrieb festgelegt.',
    quote: 'Angebot', estimateNote: 'Richtpreis — endgultiger Preis nach Begutachtung vor Ort',
    distance: 'Fahrtkosten', labor: 'Arbeitslohn', materials: 'Materialien', subtotal: 'Zwischensumme', vat: 'MwSt. (19%)', total: 'Gesamt inkl. MwSt.',
    accept: 'Angebot annehmen und bestellen',
    fallback: 'Angebot basierend auf Schatzung. Kontaktieren Sie uns fur einen genauen Preis.',
    instructions: 'Geben Sie die Montageadresse ein, wahlen Sie die Art der Arbeiten und beschreiben Sie den Auftrag. Sie erhalten einen Richtpreis.',
    perKm: '/km', returnTrip: 'Ruckfahrt', hours: 'Std', perHour: '/Std',
    includingTravel: 'inkl. Fahrzeit',
  },
  en: {
    title: 'Installation & Assembly', subtitle: 'Get your products professionally installed by a sign company near you.',
    location: 'Address', locationPlaceholder: 'Street + number',
    city: 'City', postalCode: 'Postal code', country: 'Country',
    workType: 'Type of work', workTypePlaceholder: 'Choose the type of installation...',
    description: 'Description', descriptionPlaceholder: 'Describe what needs to be installed. Be as specific as possible about dimensions, height, materials etc.',
    productInfo: 'Product(s)', productInfoPlaceholder: 'What needs to be installed? E.g.: "Lightbox 200x100cm, full-color"',
    height: 'Working height', heightLabel: 'Up to what height will work be done?', heightOptions: ['< 2.5m (ladder)', '2.5m - 5m (scaffold)', '5m - 10m (cherry picker)', '> 10m (specialist lift)'],
    surface: 'Surface', surfaceLabel: 'What surface will the installation be on?', surfaceOptions: ['Brick / masonry', 'Concrete', 'Wood', 'Metal / aluminium', 'Glass', 'Interior wall (drywall/plaster)', 'Ceiling / suspended ceiling', 'Other / unknown'],
    access: 'Accessibility', accessLabel: 'How accessible is the installation site?', accessOptions: ['Easily accessible (ground floor)', 'First floor (ladder/scaffold)', 'Only accessible with lift', 'Hard to reach', 'Indoor (normal access)', 'Indoor (tight passage)'],
    calculate: 'Calculate quote', calculating: 'Calculating...',
    loading: 'Calculating your quote...',
    disclaimer: '* This is an estimate based on the information you provided. If the actual situation differs from your description, the final invoice may be higher. The final price will be determined after inspection by the sign company.',
    quote: 'Quote', estimateNote: 'Estimate — final price after on-site inspection',
    distance: 'Travel costs', labor: 'Labor', materials: 'Materials', subtotal: 'Subtotal', vat: 'VAT (21%)', total: 'Total incl. VAT',
    accept: 'Accept quote and order',
    fallback: 'Quote based on estimate. Contact us for an exact price.',
    instructions: 'Enter the installation address, choose the type of work and describe the job. You will receive an estimate.',
    perKm: '/km', returnTrip: 'return', hours: 'hrs', perHour: '/hr',
    includingTravel: 'incl. travel time',
  },
  fr: {
    title: 'Montage & Installation', subtitle: 'Faites installer vos produits par un professionnel pres de chez vous.',
    location: 'Adresse', locationPlaceholder: 'Rue + numero',
    city: 'Ville', postalCode: 'Code postal', country: 'Pays',
    workType: 'Type de travaux', workTypePlaceholder: 'Choisissez le type de montage...',
    description: 'Description', descriptionPlaceholder: 'Décrivez ce qui doit être installé. Soyez aussi précis que possible sur les dimensions, hauteur, matériaux etc.',
    productInfo: 'Produit(s)', productInfoPlaceholder: 'Quoi installer ? Ex.: "Enseigne lumineuse 200x100cm"',
    height: 'Hauteur de travail', heightLabel: 'Jusqu a quelle hauteur travaillerez-vous ?', heightOptions: ['< 2.5m (echelle)', '2.5m - 5m (echafaudage)', '5m - 10m (nacelle)', '> 10m (nacelle specialisee)'],
    surface: 'Surface', surfaceLabel: 'Sur quelle surface sera fixe le produit ?', surfaceOptions: ['Brique / maconnerie', 'Beton', 'Bois', 'Metal / aluminium', 'Verre', 'Mur interieur (placo/platre)', 'Plafond / faux plafond', 'Autre / inconnu'],
    access: 'Accessibilite', accessLabel: 'Le lieu de pose est-il accessible ?', accessOptions: ['Facilement accessible (rez-de-chaussee)', 'Premier etage (echelle/echafaudage)', 'Uniquement avec nacelle', 'Difficile d acces', 'Interieur (acces normal)', 'Interieur (passage etroit)'],
    calculate: 'Calculer le devis', calculating: 'Calcul...',
    loading: 'Calcul de votre devis...',
    disclaimer: '* Ceci est une estimation basee sur les informations fournies. Si la situation réelle differe de votre description, la facture finale peut être plus elevee. Le prix definitif sera fixe après inspection par l’entreprise.',
    quote: 'Devis', estimateNote: 'Estimation — prix definitif apres inspection sur place',
    distance: 'Frais de deplacement', labor: 'Main d oeuvre', materials: 'Materiaux', subtotal: 'Sous-total', vat: 'TVA (20%)', total: 'Total TTC',
    accept: 'Accepter le devis',
    fallback: 'Devis base sur estimation. Contactez-nous pour un prix exact.',
    instructions: 'Entrez l adresse, choisissez le type de travaux et decrivez la prestation. Vous recevrez une estimation.',
    perKm: '/km', returnTrip: 'aller-retour', hours: 'h', perHour: '/h',
    includingTravel: 'temps de trajet inclus',
  },
};

const workTypes: Record<string, Record<Locale, string>> = {
  'lightbox_install': { nl: 'Lichtbak plaatsen (buiten)', de: 'Leuchtkasten anbringen (außen)', en: 'Lightbox installation (outdoor)', fr: 'Pose enseigne lumineuse (exterieur)' },
  'lightbox_replace': { nl: 'Lichtbak vervangen/onderhoud', de: 'Leuchtkasten ersetzen/Wartung', en: 'Lightbox replacement/maintenance', fr: 'Remplacement/maintenance enseigne lumineuse' },
  'facade_sign': { nl: 'Gevelreclame montage', de: 'Fassadenwerbung Montage', en: 'Facade signage installation', fr: 'Pose signaletique facade' },
  'window_foil': { nl: 'Raamsticker/folie aanbrengen', de: 'Fensterfolie anbringen', en: 'Window film/graphics application', fr: 'Pose film/vitrophanie' },
  'banner_outdoor': { nl: 'Banner/spandoek ophangen (buiten)', de: 'Banner/Spannband aufhangen (aussen)', en: 'Banner hanging (outdoor)', fr: 'Banderole suspendue (exterieur)' },
  'flag_pole': { nl: 'Vlaggenmast plaatsen', de: 'Fahnenmast aufstellen', en: 'Flagpole installation', fr: 'Pose mat de drapeau' },
  'bouwhek': { nl: 'Bouwhekdoek bevestigen', de: 'Bauzaunplane befestigen', en: 'Construction fence banner', fr: 'Fixation bache chantier' },
  'wall_decor': { nl: 'Wanddecoratie/behang aanbrengen', de: 'Wanddeko/Tapete anbringen', en: 'Wall decoration/wallpaper', fr: 'Pose decoration murale/papier peint' },
  'interior_sign': { nl: 'Interieur signing (kantoor/winkel)', de: 'Innenbeschilderung (Buro/Laden)', en: 'Interior signage (office/shop)', fr: 'Signaletique interieure (bureau/boutique)' },
  'car_wrap': { nl: 'Autobelettering aanbrengen', de: 'Autobeschriftung anbringen', en: 'Vehicle lettering application', fr: 'Pose lettrage vehicule' },
  'display_stand': { nl: 'Display/beursstand opbouwen', de: 'Display/Messestand aufbauen', en: 'Display/exhibition stand assembly', fr: 'Montage stand/exposition' },
  'floor_sticker': { nl: 'Vloersticker aanbrengen', de: 'Bodenaufkleber anbringen', en: 'Floor sticker application', fr: 'Pose sticker sol' },
  'led_letters': { nl: 'Losse letters/logos plaatsen', de: 'Einzelbuchstaben/Logos anbringen', en: 'Individual letters/logos', fr: 'Pose lettres/logos individuels' },
  'stretch_frame': { nl: 'Spanframe/doek spannen', de: 'Rahmenbespannung montieren', en: 'Stretch frame installation', fr: 'Montage cadre tendu' },
  'other': { nl: 'Anders / overleg nodig', de: 'Anderes / Absprache notig', en: 'Other / consultation needed', fr: 'Autre / consultation necessaire' },
};

const countries = [
  { code: 'NL', name: { nl: 'Nederland', de: 'Niederlande', en: 'Netherlands', fr: 'Pays-Bas' } },
  { code: 'BE', name: { nl: 'België', de: 'Belgien', en: 'Belgium', fr: 'Belgique' } },
  { code: 'DE', name: { nl: 'Duitsland', de: 'Deutschland', en: 'Germany', fr: 'Allemagne' } },
  { code: 'FR', name: { nl: 'Frankrijk', de: 'Frankreich', en: 'France', fr: 'France' } },
];

const KM_RATE = 0.35;

function MontagePageContent() {
  const locale = useLocale();
  const safeLocale: Locale = locale in copy ? (locale as Locale) : 'en';
  const searchParams = useSearchParams();
  const t = copy[safeLocale];

  const [step, setStep] = useState<'form' | 'loading' | 'quote'>('form');
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    address: '', city: '', postalCode: '', country: 'NL',
    workType: '', height: '', surface: '', access: '',
    workDescription: '', productInfo: '',
  });
  const [quote, setQuote] = useState<any>(null);
  const [error, setError] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  // Pre-fill product info from URL params
  useEffect(() => {
    const product = searchParams?.get('product');
    if (product && !form.productInfo) setForm(p => ({ ...p, productInfo: product }));
  }, [searchParams]);

  const vatRate = { NL: 0.21, BE: 0.21, DE: 0.19, FR: 0.20 }[form.country] || 0.21;
  const update = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const selectedWorkTypeLabel = form.workType ? (workTypes[form.workType]?.[safeLocale] || form.workType) : null;
  const workDescriptionFull = useMemo(() => {
    const parts = [form.workDescription];
    if (selectedWorkTypeLabel) parts.unshift(`Type: ${selectedWorkTypeLabel}`);
    if (form.height) parts.push(`Werkhoogte: ${form.height}`);
    if (form.surface) parts.push(`Ondergrond: ${form.surface}`);
    if (form.access) parts.push(`Toegankelijkheid: ${form.access}`);
    return parts.filter(Boolean).join(' | ');
  }, [form.workDescription, form.height, form.surface, form.access, selectedWorkTypeLabel]);

  async function handleCalculate(e: FormEvent) {
    e.preventDefault();
    if (!form.name || !form.email || !form.address || !form.workDescription.trim()) {
      setError('Vul minimaal je naam, e-mail, adres, type werkzaamheden en een beschrijving in.');
      return;
    }
    setError('');
    setStep('loading');

    try {
      const res = await fetch('/api/montage/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: form.name,
          customerEmail: form.email,
          customerPhone: form.phone,
          address: form.address,
          city: form.city || 'Onbekend',
          postalCode: form.postalCode || '0000AA',
          country: form.country,
          workDescription: workDescriptionFull,
          productInfo: form.productInfo,
          latitude: undefined, longitude: undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setQuote(data.quote);
        setStep('quote');
      } else {
        setError(data.error || 'Berekenen mislukt');
        setStep('form');
      }
    } catch {
      setError('Netwerkfout. Probeer opnieuw.');
      setStep('form');
    }
  }

  const formatPrice = (p: number) => `€${p.toFixed(2).replace('.', ',')}`;

  function downloadPDF() {
    if (!quote) return;
    const vatPct = Math.round(vatRate * 100);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Offerte Montage</title>
<style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto;color:#1a1a1a;line-height:1.5}
h1{font-size:22px;margin-bottom:4px}h2{font-size:16px;color:#666;font-weight:400;margin-bottom:20px}
table{width:100%;border-collapse:collapse;margin:16px 0}td,th{padding:8px 12px;text-align:left;border-bottom:1px solid #e5e5e5}
.total{font-size:18px;font-weight:700;color:#166534}.disclaimer{font-size:11px;color:#999;margin-top:24px;border-top:1px solid #eee;padding-top:12px}
.company{font-size:12px;color:#999;margin-top:32px}</style></head><body>
<h1>Offerte Montage & Installatie</h1><h2>Printen.eu — ${new Date().toLocaleDateString('nl-NL')}</h2>
<p><strong>Locatie:</strong> ${form.address}, ${form.postalCode} ${form.city}, ${form.country}</p>
${form.productInfo ? `<p><strong>Product:</strong> ${form.productInfo}</p>` : ''}
<p><strong>Werkzaamheden:</strong> ${form.workDescription || '-'}</p>
<table>
<tr><td>Reiskosten (${quote.distanceKm} km retour)</td><td style="text-align:right">${formatPrice(quote.kmCost)}</td></tr>
<tr><td>Arbeidsloon (${quote.estimatedHours} uur x ${formatPrice(quote.hourlyRate)}/uur)</td><td style="text-align:right">${formatPrice(quote.laborCost)}</td></tr>
${(quote.materials || []).map((m: any) => `<tr><td>${m.quantity}x ${m.name} (${formatPrice(m.unitPrice)}/st)</td><td style="text-align:right">${formatPrice(m.quantity * m.unitPrice)}</td></tr>`).join('')}
<tr><td><strong>Subtotaal</strong></td><td style="text-align:right"><strong>${formatPrice(quote.subtotal)}</strong></td></tr>
<tr><td>BTW (${vatPct}%)</td><td style="text-align:right">${formatPrice(quote.vat)}</td></tr>
<tr class="total"><td><strong>Totaal incl. BTW</strong></td><td style="text-align:right"><strong>${formatPrice(quote.total)}</strong></td></tr>
</table>
${quote.notes ? `<p><em>${quote.notes}</em></p>` : ''}
<p class="disclaimer">Dit is een richtprijs op basis van de door jou verstrekte informatie. Als de werkelijke situatie afwijkt kan de eindfactuur hoger uitvallen. De definitieve prijs wordt vastgesteld na inspectie door het signbedrijf.</p>
<p class="company">Printen.eu — Onderdeel van Twilper.com — Merelstraat 64, 8916 AX Leeuwarden — KVK 98440217 — BTW NL005332508B94 — info@printen.eu</p>
</body></html>`;
    const w = window.open('', '_blank', 'width=800,height=600');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 500); }
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.title}</h1>
      <p className="text-gray-600 mb-8">{t.subtitle}</p>

      {step === 'form' && (
        <Card className="p-6">
          <p className="text-sm text-gray-500 mb-6">{t.instructions}</p>
          <form onSubmit={handleCalculate} className="space-y-4">
            {/* Contactgegevens — eerst invullen */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-bold text-blue-900 mb-3">Uw gegevens</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1"><label className="text-xs font-medium mb-1 block">Naam *</label><Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="Uw naam" required /></div>
                <div className="col-span-2 sm:col-span-1"><label className="text-xs font-medium mb-1 block">E-mail *</label><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="uw@email.nl" required /></div>
                <div className="col-span-2"><label className="text-xs font-medium mb-1 block">Telefoon</label><Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="06 12345678" /></div>
              </div>
            </div>

            {/* Montage adres */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium mb-1 block">{t.location} *</label>
                <Input value={form.address} onChange={e => update('address', e.target.value)} placeholder={t.locationPlaceholder} required />
              </div>
              <div><label className="text-sm font-medium mb-1 block">{t.postalCode}</label><Input value={form.postalCode} onChange={e => update('postalCode', e.target.value)} /></div>
              <div><label className="text-sm font-medium mb-1 block">{t.city}</label><Input value={form.city} onChange={e => update('city', e.target.value)} /></div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t.country}</label>
              <select className="w-full rounded-lg border border-gray-300 p-2 text-sm" value={form.country} onChange={e => update('country', e.target.value)}>
                {countries.map(c => <option key={c.code} value={c.code}>{c.name[safeLocale] || c.name.nl}</option>)}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">{t.workType} *</label>
              <select className="w-full rounded-lg border border-gray-300 p-2 text-sm" value={form.workType} onChange={e => update('workType', e.target.value)} required>
                <option value="">{t.workTypePlaceholder}</option>
                {Object.entries(workTypes).map(([key, names]) => (
                  <option key={key} value={key}>{names[safeLocale] || names.nl}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">{t.height}</label>
                <select className="w-full rounded-lg border border-gray-300 p-2 text-sm" value={form.height} onChange={e => update('height', e.target.value)}>
                  <option value="">{t.heightLabel}</option>
                  {t.heightOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{t.surface}</label>
                <select className="w-full rounded-lg border border-gray-300 p-2 text-sm" value={form.surface} onChange={e => update('surface', e.target.value)}>
                  <option value="">{t.surfaceLabel}</option>
                  {t.surfaceOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{t.access}</label>
                <select className="w-full rounded-lg border border-gray-300 p-2 text-sm" value={form.access} onChange={e => update('access', e.target.value)}>
                  <option value="">{t.accessLabel}</option>
                  {t.accessOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">{t.productInfo}</label>
              <Input value={form.productInfo} onChange={e => update('productInfo', e.target.value)} placeholder={t.productInfoPlaceholder} />
              <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                <AlertCircle className="h-3 w-3 flex-none mt-0.5" />
                {{ nl: 'Het product zelf bestel je apart via de productpagina. Deze offerte is alleen voor de montage.',
                   de: 'Das Produkt selbst bestellen Sie separat uber die Produktseite. Dieses Angebot gilt nur fur die Montage.',
                   en: 'The product itself must be ordered separately on the product page. This quote is for installation only.',
                   fr: 'Le produit lui-même doit être commande séparément sur la page produit. Ce devis concerne uniquement le montage.'
                }[safeLocale]}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t.description} *</label>
              <Textarea value={form.workDescription} onChange={e => update('workDescription', e.target.value)} placeholder={t.descriptionPlaceholder} rows={4} required />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-none mt-0.5" />
              <p className="text-xs text-amber-800">{t.disclaimer}</p>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="submit" className="w-full" size="lg"><Calculator className="h-4 w-4 mr-2" />{t.calculate}</Button>
          </form>
        </Card>
      )}

      {step === 'loading' && (
        <Card className="p-12 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700">{t.loading}</p>
          <p className="text-sm text-gray-500 mt-2">Dit duurt ongeveer 5-10 seconden.</p>
        </Card>
      )}

      {step === 'quote' && quote && (
        <div className="space-y-6">
          <Card className="p-6 border-2 border-green-200 bg-green-50">
            <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2"><Euro className="h-5 w-5 text-green-600" />{t.quote}</h2>
            <Badge variant="secondary" className="mb-4 bg-amber-100 text-amber-800 text-xs">{t.estimateNote}</Badge>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1"><Truck className="h-4 w-4 text-gray-400" />{t.distance} ({quote.distanceKm} km {t.returnTrip})</span>
                <span className="font-medium">{formatPrice(quote.kmCost)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1"><Wrench className="h-4 w-4 text-gray-400" />{t.labor} ({quote.estimatedHours} {t.hours} x {formatPrice(quote.hourlyRate)}{t.perHour}, {t.includingTravel})</span>
                <span className="font-medium">{formatPrice(quote.laborCost)}</span>
              </div>
              {quote.materials?.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-sm font-medium mb-2">{t.materials}</p>
                  {quote.materials.map((m: any, i: number) => (
                    <div key={i} className="flex justify-between text-sm text-gray-600 ml-4"><span>{m.quantity}x {m.name}</span><span>{formatPrice(m.quantity * m.unitPrice)}</span></div>
                  ))}
                </div>
              )}
              <div className="border-t pt-3 flex justify-between text-sm"><span>{t.subtotal}</span><span className="font-medium">{formatPrice(quote.subtotal)}</span></div>
              <div className="flex justify-between text-sm text-gray-500"><span>{t.vat}</span><span>{formatPrice(quote.vat)}</span></div>
              <div className="border-t pt-3 flex justify-between text-lg font-bold"><span>{t.total}</span><span className="text-green-700">{formatPrice(quote.total)}</span></div>
            </div>

            {quote.confidence === 'low' && <Badge variant="secondary" className="mt-4 bg-yellow-100 text-yellow-800">{t.fallback}</Badge>}
            {quote.notes && <p className="text-xs text-gray-500 mt-3 italic">{quote.notes}</p>}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-none mt-0.5" />
              <p className="text-xs text-amber-800">{t.disclaimer}</p>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" size="lg" onClick={downloadPDF}><Download className="h-4 w-4 mr-2" />PDF</Button>
              <Button className="flex-1" size="lg" disabled={payLoading} onClick={async () => {
                setPayLoading(true);
                try {
                  const res = await fetch('/api/montage/order', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ quoteId: quote.id, customerName: form.name, customerEmail: form.email, customerPhone: form.phone, locale }),
                  });
                  const data = await res.json();
                  if (data.url) window.location.href = data.url;
                  else setError(data.error || 'Betaling mislukt');
                } catch { setError('Netwerkfout'); }
                setPayLoading(false);
              }}>
                {payLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                {payLoading ? 'Bezig...' : `Betalen — ${formatPrice(quote.total)}`}
              </Button>
            </div>

            <button type="button" className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700" onClick={() => setStep('form')}>Opnieuw berekenen</button>
          </Card>
        </div>
      )}
    </div>
  );
}

export default function MontagePage() {
  return (
    <Suspense fallback={null}>
      <MontagePageContent />
    </Suspense>
  );
}
